import { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Link from "@mui/material/Link";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { StyledSwitch } from "../../components/Base";
import Button from "../../components/Button";
import Confirm from "../../components/Confirm";
import courseCategoryService from "../../services/courseCategory";
import { CourseResponse } from "../../api/courses";

interface MoodleCourseItemProps {
  onEdit: (id: number) => void;
  onVisibleChange: (id: number, newVisible: number) => void;
  onShowChange: (id: number, newShow: number) => void;
  onIsRefresherChange: (id: number, newValue: boolean) => void;
  onArchive: (id: number) => void;
  course: CourseResponse;
  isCompanyCourse?: boolean;
}

const MoodleCourseItem: React.FC<MoodleCourseItemProps> = ({
  onEdit,
  onVisibleChange,
  onShowChange,
  onIsRefresherChange,
  onArchive,
  course,
  isCompanyCourse = false,
}) => {
  const { t } = useTranslation();
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  const refreshersList = course.refreshers ? (
    <ul>
      {course.refreshers.map((course) => (
        <li key={course.id}>
          ({course.id}) {course.name}
        </li>
      ))}
    </ul>
  ) : null;

  function timestampToStringDate(timestamp: number) {
    if (!timestamp) return "-";
    return moment.unix(timestamp).utc().format("DD.MM.YYYY");
  }

  return (
    <>
      <TableRow>
        <TableCell>{course.id}</TableCell>
        <TableCell>{course.displayName}</TableCell>
        <TableCell>{course.summary}</TableCell>
        <TableCell>{timestampToStringDate(course.startDate)}</TableCell>
        {isCompanyCourse ? (
          <>
            <TableCell>
              <StyledSwitch
                title={t("course.change_company_course_visibility")}
                checked={!!course.visible}
                onChange={() => course.id !== undefined && onVisibleChange(course.id, course.visible ? 0 : 1)}
              />
            </TableCell>
            <TableCell>
              <StyledSwitch
                title={t("course.mark_as_refresher")}
                checked={!!course.isRefresher}
                onChange={() => course.id !== undefined && onIsRefresherChange(course.id, !course.isRefresher)}
              />
            </TableCell>
          </>
        ) : (
          <>
            <TableCell>
              <StyledSwitch
                title={t("course.change_moodle_course_visibility")}
                checked={!!course.visible}
                onChange={() => course.id !== undefined && onVisibleChange(course.id, course.visible ? 0 : 1)}
              />
            </TableCell>
            <TableCell>
              <StyledSwitch
                title={t("course.change_show_at_all_courses")}
                checked={!!course.showAtAllCourses}
                onChange={() => course.id !== undefined && onShowChange(course.id, course.showAtAllCourses ? 0 : 1)}
              />
            </TableCell>
            <TableCell>{refreshersList}</TableCell>
          </>
        )}
        <TableCell>{course.isConfigured ? t("course.configured") : t("course.not_configured")}</TableCell>
        <TableCell>{courseCategoryService.getByCode(course.courseCategory)?.getDisplayName(t)}</TableCell>
        <TableCell>
          <Link style={{ marginRight: "0.5rem" }} onClick={() => course.id !== undefined && onEdit(course.id)}>
            <Button
              title={t(isCompanyCourse ? "course.edit_company_lms_action" : "course.edit_moodle_action")}
              icon={<EditIcon />}
            />
          </Link>
          {isCompanyCourse && (
            <Button
              title={t("course.delete_course_action")}
              icon={<DeleteIcon />}
              onClick={() => setShowArchiveConfirm(true)}
            />
          )}
        </TableCell>
      </TableRow>
      {showArchiveConfirm && (
        <Confirm
          title={t("course.delete_course_action")}
          onConfirm={(confirmed) => {
            setShowArchiveConfirm(false);
            if (confirmed && course.id !== undefined) {
              onArchive(course.id);
            }
          }}
        >
          {t("course.archive_confirm_prefix")} <strong>{course.displayName}</strong>?{" "}
          {t("course.archive_confirm_suffix")}
        </Confirm>
      )}
    </>
  );
};

export default MoodleCourseItem;
